/**
 * MechanicActiveJobBanner
 * -----------------------------------------------------------------------------
 * Live banner shown at the top of the Mechanic Dashboard when the mechanic
 * has a clocked-in job. Polls /api/mechanic/active-job every 15 s and ticks
 * the running-time display every second.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Timer, ArrowRight, StopCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtElapsed = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m.toString().padStart(2, '0')}m`;
};

export default function MechanicActiveJobBanner() {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/mechanic/active-job`, { credentials: 'include' });
      if (!res.ok) return;
      const j = await res.json();
      setActive(j.active || null);
    } catch (_e) { /* swallow */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  // 1-second ticker for the live timer
  useEffect(() => {
    if (!active?.started_at) return undefined;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [active?.started_at]);

  const clockOut = async () => {
    if (!active) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/job-cards/${active.job_card_id}/clock-out`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Clocked out');
      await load();
    } catch (e) {
      toast.error(`Clock out failed: ${e.message || e}`);
    }
  };

  if (!active) return null;

  const startMs = new Date(active.started_at).getTime();
  const elapsedMin = Math.max(0, Math.floor((Date.now() - startMs) / 60000 + (tick * 0)));
  const quotedMin = Math.round((active.quoted_hours || 0) * 60);
  const overBudget = quotedMin > 0 && elapsedMin > quotedMin;

  return (
    <div
      className={`rounded-xl border-2 p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
        overBudget
          ? 'border-rose-300 bg-gradient-to-r from-rose-50 to-orange-50'
          : 'border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50'
      }`}
      data-testid="mechanic-active-job-banner"
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${overBudget ? 'bg-rose-200' : 'bg-amber-200'}`}>
        <Timer className={`h-5 w-5 ${overBudget ? 'text-rose-700 animate-pulse' : 'text-amber-700 animate-pulse'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[10px] uppercase tracking-wider font-bold ${overBudget ? 'text-rose-700' : 'text-amber-700'}`}>
          Clocked in · running
        </p>
        <p className="text-base font-semibold text-slate-900 truncate">
          {active.vehicle_rego || 'No rego'} · {active.customer_name || 'Customer'} · {active.job_card_number}
        </p>
        <p className="text-xs text-slate-600 mt-0.5">
          <strong className={overBudget ? 'text-rose-700' : 'text-amber-800'}>{fmtElapsed(elapsedMin)}</strong> elapsed
          {quotedMin > 0 && (
            <>
              {' '}of <strong>{fmtElapsed(quotedMin)}</strong> quoted
              {overBudget && <span className="ml-1 text-rose-700 font-semibold">· over budget</span>}
            </>
          )}
        </p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={clockOut}
          className="px-3 py-1.5 rounded-md bg-white hover:bg-slate-100 border border-slate-300 text-xs font-semibold flex items-center gap-1.5"
          data-testid="active-job-clock-out"
        >
          <StopCircle className="h-3.5 w-3.5" />
          Clock out
        </button>
        <button
          onClick={() => navigate(`/job-cards?focus=${active.job_card_id}`)}
          className="px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold flex items-center gap-1.5"
          data-testid="active-job-open"
        >
          Open
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
